<template>
    <div class="calendar-legend">
        <DateAvailability showAll />
        <div v-if="attributes" class="info">Click on a date for availability details</div>
    </div>
    <VCalendar
        :min-date="new Date()"
        :locale="{ id: 'en', firstDayOfWeek: 2, masks: { weekdays: 'WWW' } }"
        :attributes="attributes"
        @update:pages="monthChange"
    >
        <template v-slot:day-content="{ day, attributes }">
            <div class="date-details" :class="{ 'has-departures': dateHasDepartures(attributes) }">
                <div
                    v-for="(attr, i) in attributes"
                    @click="displayDate(attr)"
                    :key="i"
                    class="availability"
                    :class="attr.customData ? attr.customData.class : ''"
                >
                    <div v-if="attr.customData" class="icons">
                        <div v-if="!attr.customData.fitsVehicle && hasVehicle" class="icon">
                            <img src="@/assets/icons/Cardeck-is-full.svg" class="icon__svg" />
                        </div>
                        <div v-if="attr.customData.fitsVehicle && hasVehicle" class="icon">
                            <img src="@/assets/car-available.svg" class="icon__svg" />
                        </div>
                    </div>
                    <p v-if="hasCabin && attr.customData" class="availability-rooms" :class="attr.customData.class">
                        {{ attr.customData.cabins }} Cabins
                    </p>
                </div>
                <span>{{ day.day }}.</span>
            </div>
        </template>
    </VCalendar>
    <VModal
        v-if="showDateDetails"
        class="availability-lightbox"
        @close="showDateDetails = false"
        :title="dateToDisplay?.dates ? dateFormat(dateToDisplay?.dates) : ''"
    >
        <DateAvailability :details="dateToDisplay" :hasVehicle="hasVehicle" :hasCabin="hasCabin"></DateAvailability>
    </VModal>
</template>

<script setup lang="ts">
import { dateFormat } from "@/helpers/dateHelpers";
import { AvailabilityDate, monthChangeEvent } from "@/modules/AvailabilityCalendar/models/AvailabilityDate";
import { VCalendar, VModal } from "v-ferry-components";
import { ref } from "vue";
import { Attribute } from "../models/AvailabilityCalendarTypes";
import DateAvailability from "./DateAvailability.vue";

interface Props {
    attributes?: AvailabilityDate[];
    hasVehicle?: boolean;
    hasCabin?: boolean;
}

defineProps<Props>();

const emit = defineEmits(["dateChange"]);

const calendarDate = ref<monthChangeEvent>();

const isDisabled = (attr: Attribute) => {
    return attr.key === "disabled";
};

const monthChange = (newDate: monthChangeEvent[]) => {
    const selectedMonth = newDate[0];
    const isDuplicateEvent = calendarDate.value?.month === selectedMonth.month && calendarDate.value?.year === selectedMonth.year;

    if (!isDuplicateEvent) {
        calendarDate.value = selectedMonth;
        emit("dateChange", selectedMonth);
    }
};

const dateHasDepartures = (attrs: Attribute[]) => {
    return attrs.length ? !isDisabled(attrs[0]) : false;
};

const showDateDetails = ref(false);

const dateToDisplay = ref<Attribute>();

const displayDate = (attr: Attribute) => {
    if (isDisabled(attr)) return;
    dateToDisplay.value = attr;
    showDateDetails.value = true;
};
</script>

<style lang="scss">
.date-details__isLoading {
    border-radius: 0.5rem;
    width: 100%;
    height: 100%;
    background-color: var(--c-base);
    position: absolute;
    top: 0;
    z-index: 100;
    opacity: 0.8;
}
.availability-lightbox {
    .details {
        .icon {
            width: 20px !important;
            height: 20px !important;
        }
    }
}
</style>
